import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Circle, Sparkles, ChevronUp, ChevronDown, MessageSquare, TrendingUp, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/auth-context';
import { useSite } from '@/contexts/SiteContext';
import { API_URL } from '@/config/api';
import { authFetch } from '@/utils/auth-fetch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  completedAt?: string;
}

interface ConversationData {
  daily: number;
  weekly: number;
  monthly: number;
  allTime: number;
  chartData: Array<{
    date: string;
    conversations: number;
  }>;
}

export default function OnboardingProgress() {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'daily' | 'weekly' | 'last30days' | 'monthly' | 'all-time'>('last30days');
  const [conversationData, setConversationData] = useState<ConversationData | null>(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: 'add_site',
      title: '1) Add your site',
      description: 'Add your website to PageMind',
      completed: false
    },
    {
      id: 'crawl_analyze',
      title: '2) Crawl & analyze a few pages',
      description: 'Let PageMind understand your site structure',
      completed: false
    },
    {
      id: 'simulate_site',
      title: '3) Simulate your site',
      description: 'Test PageMind features on your site',
      completed: false
    },
    {
      id: 'visit_admin',
      title: '4) Visit Admin page',
      description: 'Explore the admin dashboard',
      completed: false
    },
    {
      id: 'meet_crew',
      title: '5) Meet the Crew',
      description: 'Discover all PageMind agents',
      completed: false
    },
    {
      id: 'talk_to_agents',
      title: '6) Talk to your agents',
      description: 'Chat with your agents and get them to know you',
      completed: false
    },
    {
      id: 'install_pagemind',
      title: '7) Install PageMind in your site',
      description: 'Add PageMind script to your website',
      completed: false
    },
    {
      id: 'learn_analytics',
      title: '8) Learn your site analytics',
      description: 'Understand your site performance',
      completed: false
    },
    {
      id: 'watch_experiences',
      title: '9) Watch your customer experiences',
      description: 'View real user sessions',
      completed: false
    },
    {
      id: 'understand_behavior',
      title: '10) Understand your customer behavior',
      description: 'Analyze user patterns and insights',
      completed: false
    },
    {
      id: 'measure_impact',
      title: '11) Visit Growth',
      description: 'Track your growth and see PageMind\'s impact on your business',
      completed: false
    },
    {
      id: 'invite_friend',
      title: '12) Invite a friend and activate your benefits',
      description: 'Share PageMind and unlock rewards',
      completed: false
    }
  ]);

  // Load user preferences including collapsed state
  useEffect(() => {
    if (user && !preferencesLoaded) {
      loadUserPreferences();
    }
  }, [user, preferencesLoaded]);

  // Reload preferences when site changes
  useEffect(() => {
    if (user && selectedSite && preferencesLoaded) {
      loadUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.id, selectedSite?._id, user, preferencesLoaded]);

  // Force refresh on component mount after a short delay
  useEffect(() => {
    if (user && selectedSite) {
      const mountTimer = setTimeout(() => {
        fetchOnboardingProgress();
      }, 500);
      
      return () => clearTimeout(mountTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch onboarding progress from backend when user or site changes
    // Only fetch after preferences are loaded to ensure we have the correct state
    if (user && selectedSite && preferencesLoaded) {
      // Add a small delay to ensure site context is fully updated
      const timer = setTimeout(() => {
        fetchOnboardingProgress();
        fetchConversationData();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, selectedSite, timePeriod, preferencesLoaded]);

  const loadUserPreferences = async () => {
    console.log('[OnboardingProgress] Loading user preferences...', { selectedSite: selectedSite?.id || selectedSite?._id });
    try {
      const response = await authFetch('/users/me/preferences');

      if (response.ok) {
        const data = await response.json();
        console.log('[OnboardingProgress] Loaded preferences:', data);
        if (data.onboardingCollapsed) {
          if (selectedSite) {
            const siteId = selectedSite._id || selectedSite.id;
            console.log('[OnboardingProgress] Checking collapsed state for site:', siteId);
            if (data.onboardingCollapsed[siteId] !== undefined) {
              console.log('[OnboardingProgress] Setting collapsed state to:', data.onboardingCollapsed[siteId]);
              const collapsedValue = data.onboardingCollapsed[siteId];
              setIsManuallyCollapsed(collapsedValue);
              setIsCollapsed(collapsedValue); // Also set isCollapsed to ensure immediate visual state
            }
          } else {
            console.log('[OnboardingProgress] No selectedSite available yet, will check again when site is selected');
          }
        }
        setPreferencesLoaded(true);
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      setPreferencesLoaded(true);
    }
  };

  const saveCollapsedPreference = async (collapsed: boolean) => {
    if (!selectedSite || !user) return;

    try {
      const siteId = selectedSite._id || selectedSite.id;
      console.log('[OnboardingProgress] Saving collapsed preference:', { siteId, collapsed });
      const response = await authFetch('/users/me/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          onboardingCollapsed: {
            [siteId]: collapsed
          }
        })
      });
      
      if (response.ok) {
        console.log('[OnboardingProgress] Successfully saved collapsed preference');
      } else {
        console.error('[OnboardingProgress] Failed to save preference:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to save collapsed preference:', error);
    }
  };

  const fetchOnboardingProgress = async () => {
    if (!user || !selectedSite) return;

    try {
      const siteId = selectedSite._id || selectedSite.id;
      console.log('[OnboardingProgress] Fetching progress for site:', siteId);
      const response = await authFetch(`/onboarding/progress/${siteId}`);

      if (response.ok) {
        const data = await response.json();
        console.log('[OnboardingProgress] Received progress data:', data);
        setSteps(prevSteps => 
          prevSteps.map(step => ({
            ...step,
            completed: data.completedSteps?.includes(step.id) || false,
            completedAt: data.stepDetails?.[step.id]?.completedAt
          }))
        );
      }
    } catch (error) {
      console.error('Failed to fetch onboarding progress:', error);
    }
  };

  const fetchConversationData = async () => {
    if (!selectedSite) return;

    try {
      const siteId = selectedSite._id || selectedSite.id;
      const days = timePeriod === 'daily' ? 1 : 
                   timePeriod === 'weekly' ? 7 : 
                   timePeriod === 'last30days' ? 30 :
                   timePeriod === 'monthly' ? 30 : 365;
      
      // Use the new conversation-counts endpoint for more accurate data
      const response = await authFetch(`/chatbot-analytics/${siteId}/conversation-counts?days=${days}`);

      if (response.ok) {
        const data = await response.json();
        
        // Get the total conversations for the period
        const totalConversations = data.overview?.total_conversations || 0;
        
        // Format chart data from daily trends and reverse to show oldest first
        const chartData = data.dailyTrends?.map(trend => ({
          date: trend.date,
          conversations: trend.conversations || 0
        })).reverse() || [];
        
        setConversationData({
          daily: timePeriod === 'daily' ? totalConversations : 0,
          weekly: timePeriod === 'weekly' ? totalConversations : 0,
          monthly: timePeriod === 'monthly' || timePeriod === 'last30days' ? totalConversations : 0,
          allTime: timePeriod === 'all-time' ? totalConversations : 0,
          chartData: chartData
        });
      }
    } catch (error) {
      console.error('Failed to fetch conversation data:', error);
    }
  };

  const completedCount = useMemo(() => steps.filter(step => step.completed).length, [steps]);
  const progressPercentage = useMemo(() => (completedCount / steps.length) * 100, [completedCount, steps.length]);
  const allStepsCompleted = useMemo(() => completedCount === steps.length, [completedCount, steps.length]);

  // When journey is complete and user manually collapsed, we should initialize isCollapsed to true
  // Only do this after preferences are loaded to avoid race conditions
  useEffect(() => {
    if (allStepsCompleted && isManuallyCollapsed && preferencesLoaded) {
      setIsCollapsed(true);
    }
  }, [allStepsCompleted, isManuallyCollapsed, preferencesLoaded]);
  
  // Toggle handler that ensures immediate visual update
  const handleToggleCollapse = useCallback(async () => {
    if (!allStepsCompleted) {
      const newCollapsedState = !isManuallyCollapsed;
      setIsManuallyCollapsed(newCollapsedState);
      setIsCollapsed(newCollapsedState); // Ensure immediate visual update
      await saveCollapsedPreference(newCollapsedState);
    } else {
      const newCollapsedState = !isCollapsed;
      setIsCollapsed(newCollapsedState);
      setIsManuallyCollapsed(newCollapsedState); // Keep states in sync
      await saveCollapsedPreference(newCollapsedState);
    }
  }, [allStepsCompleted, isManuallyCollapsed, isCollapsed, saveCollapsedPreference]);

  // Determine if we should show collapsed view
  // Show collapsed if: user manually collapsed it, OR journey is complete and it's collapsed
  const shouldShowCollapsed = isCollapsed;

  if (!selectedSite) {
    return (
      <Card className="w-full bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800">
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            Your PageMind Journey
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Please select a site from the dropdown above to view your onboarding progress
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="w-full bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 border-purple-200 dark:border-purple-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              Your PageMind Journey
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {allStepsCompleted ? 'Congratulations! You\'ve completed your journey!' : 'Complete these steps to unlock the full power of PageMind'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold text-purple-600">{completedCount}/{steps.length}</div>
              <div className="text-sm text-muted-foreground">Steps completed</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleCollapse}
              className="ml-2"
              title={shouldShowCollapsed ? "Expand journey" : "Collapse journey"}
            >
              {shouldShowCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4 bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {!shouldShowCollapsed && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "flex items-start gap-3 p-4 rounded-lg transition-all duration-200",
                step.completed 
                  ? "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800" 
                  : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md"
              )}
            >
              <div className="mt-1">
                {step.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={cn(
                  "font-semibold text-sm",
                  step.completed ? "text-green-700 dark:text-green-300" : ""
                )}>
                  {step.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {step.description}
                </p>
                {step.completed && step.completedAt && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    ✓ Completed
                  </p>
                )}
              </div>
            </div>
          ))}
          </div>
        )}
        
        {/* Show completion message only when journey is complete and not collapsed */}
        {allStepsCompleted && !shouldShowCollapsed && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg text-center">
            <h3 className="text-lg font-bold text-purple-700 dark:text-purple-300">
              🎉 Congratulations! You've completed your PageMind journey!
            </h3>
            <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
              You're now a PageMind expert. Keep exploring and discovering new features!
            </p>
          </div>
        )}
        
        {/* Chat Analytics Section - Show when collapsed or journey complete */}
        {shouldShowCollapsed && (
          <div className="space-y-6">
              {/* Time Period Selector and Metrics */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Chat Analytics</h3>
                <Select value={timePeriod} onValueChange={(value: any) => setTimePeriod(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Today</SelectItem>
                    <SelectItem value="weekly">Last 7 days</SelectItem>
                    <SelectItem value="last30days">Last 30 days</SelectItem>
                    <SelectItem value="monthly">This Month</SelectItem>
                    <SelectItem value="all-time">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Conversation Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Conversations</p>
                      <p className="text-2xl font-bold">
                        {conversationData?.[timePeriod === 'all-time' ? 'allTime' : timePeriod === 'last30days' ? 'monthly' : timePeriod] || 0}
                      </p>
                    </div>
                    <MessageSquare className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Average per Day</p>
                      <p className="text-2xl font-bold">
                        {conversationData ? Math.round(conversationData[timePeriod === 'all-time' ? 'allTime' : timePeriod === 'last30days' ? 'monthly' : timePeriod] / (timePeriod === 'daily' ? 1 : timePeriod === 'weekly' ? 7 : timePeriod === 'last30days' ? 30 : timePeriod === 'monthly' ? 30 : 365)) : 0}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Period</p>
                      <p className="text-2xl font-bold">
                        {timePeriod === 'daily' ? 'Today' : 
                         timePeriod === 'weekly' ? 'Last 7 days' : 
                         timePeriod === 'last30days' ? 'Last 30 days' :
                         timePeriod === 'monthly' ? 'This Month' : 
                         'All Time'}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </div>
              
              {/* Conversation Chart */}
              {conversationData && conversationData.chartData.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h4 className="text-base font-semibold mb-4">Conversation Trends</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={conversationData.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="conversations" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}